import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Users,
  Target,
  TrendingUp,
  ArrowLeft,
  BarChart3,
  Award,
  GraduationCap,
  CheckCircle2,
  AlertCircle,
  UserPlus,
  Loader2,
  Trash2,
  ChevronRight
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Cell,
} from "recharts";

interface ObjectiveStat {
  objectiveId: string;
  title: string;
  description: string;
  studentsAttempted: number;
  averageMastery: number;
}

interface StudentData {
  userId: string;
  email: string;
  enrolledAt: string;
  progress: {
    completedNodes: number;
    totalNodes: number;
    progressPercentage: number;
  };
  objectiveMasteries: Array<{
    objectiveId: string;
    objectiveTitle: string;
    masteryLevel: number;
    attemptsCount: number;
    correctCount: number;
  }>;
  averageObjectiveMastery: number;
  examStats: {
    totalExamAttempts: number;
    passedExams: number;
    averageScore: number;
  };
}

interface InvitedData {
  email: string;
  invited_at: string;
}

interface NodeDifficultyStat {
  nodeId: string;
  title: string;
  attempts: number;
  uniqueStudents: number;
  passRate: number;
  avgScore: number;
}

interface DashboardData {
  course: {
    id: string;
    title: string;
    description: string;
  };
  objectives: ObjectiveStat[];
  students: StudentData[];
  invited?: InvitedData[];
  nodeDifficulty?: NodeDifficultyStat[];
  summary: {
    totalStudents: number;
    averageProgress: number;
    averageObjectiveMastery: number;
  };
}

export default function InstructorDashboard() {
  const { id: courseId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [addEmail, setAddEmail] = useState("");
  const [batchEmails, setBatchEmails] = useState("");
  const [adding, setAdding] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentData | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [removingInvite, setRemovingInvite] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (!session) {
        navigate("/auth");
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (!session) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (session && courseId && courseId !== ":id") {
      fetchDashboardData();
    } else if (courseId === ":id" || !courseId) {
      setLoading(false);
      toast.error("Invalid course ID. Please access this page from a course.");
    }
  }, [session, courseId]);

  const fetchDashboardData = async () => {
    if (!courseId || courseId === ":id") {
      toast.error("Invalid course ID");
      setLoading(false);
      return;
    }
    
    try {
      const { data, error } = await supabase.functions.invoke("get-instructor-dashboard", {
        body: { courseId },
      });
      if (error) throw error;
      setDashboardData(data);
    } catch (error: any) {
      console.error("Error fetching dashboard:", error);
      toast.error("Failed to load instructor dashboard");
    } finally {
      setLoading(false);
    }
  };

  const addStudents = async (emailsInput: string | string[]) => {
    if (!courseId || courseId === ":id" || adding) return;
    const emails = Array.isArray(emailsInput)
      ? emailsInput.map((e) => String(e).trim().toLowerCase()).filter((e) => e && e.includes("@"))
      : emailsInput.split(/[\n,;]+/).map((e) => e.trim().toLowerCase()).filter((e) => e && e.includes("@"));
    if (emails.length === 0) {
      toast.error("Enter at least one valid email");
      return;
    }
    setAdding(true);
    try {
      const { data, error } = await invokeFunction("add-course-students", {
        body: { courseId, emails },
      });
      if (error) throw new Error(typeof error === "string" ? error : (error as { message?: string })?.message || (error as { error?: string })?.error || "Failed to add students");
      const { added, invited, notFound, alreadyEnrolled } = data || {};
      if (added?.length > 0) {
        toast.success(`Added ${added.length} student(s): ${added.join(", ")}`);
        fetchDashboardData();
        setAddEmail("");
        setBatchEmails("");
      }
      if (invited?.length > 0) {
        toast.success(
          `${invited.length} invited (pending sign-up): ${invited.join(", ")}. They'll receive a sign-up link by email.`
        );
        fetchDashboardData();
        setAddEmail("");
        setBatchEmails("");
      }
      if (alreadyEnrolled?.length > 0) {
        toast.info(`${alreadyEnrolled.length} already enrolled or invited: ${alreadyEnrolled.join(", ")}`);
      }
      if (notFound?.length > 0) {
        toast.error(`Could not add: ${notFound.join(", ")}.`);
      }
      if (!added?.length && !invited?.length && !alreadyEnrolled?.length && !notFound?.length) {
        toast.error("No students were added");
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to add students");
    } finally {
      setAdding(false);
    }
  };

  const removeStudent = async (student: StudentData) => {
    if (!courseId || courseId === ":id" || removing) return;
    setRemoving(student.userId);
    try {
      const { error } = await invokeFunction("remove-course-student", {
        body: { courseId, userId: student.userId },
      });
      if (error) throw new Error(typeof error === "string" ? error : (error as { message?: string })?.message || (error as { error?: string })?.error || "Failed to remove student");
      toast.success(`Removed ${student.email} from the course`);
      setSelectedStudent(null);
      fetchDashboardData();
      queryClient.invalidateQueries({ queryKey: ["dashboard-learner-insights"] });
    } catch (err: any) {
      toast.error(err?.message || "Failed to remove student");
    } finally {
      setRemoving(null);
    }
  };

  const removeInvitation = async (email: string) => {
    if (!courseId || courseId === ":id" || removingInvite) return;
    setRemovingInvite(email);
    try {
      const { error } = await supabase
        .from("course_invitations")
        .delete()
        .eq("course_id", courseId)
        .eq("email", email);
      if (error) throw error;
      toast.success(`Invitation removed for ${email}`);
      fetchDashboardData();
    } catch (err: any) {
      toast.error(err?.message || "Failed to remove invitation");
    } finally {
      setRemovingInvite(null);
    }
  };

  if (!session) {
    return null;
  }

  const getMasteryColor = (level: number) => {
    if (level >= 80) return "text-green-600";
    if (level >= 60) return "text-yellow-600";
    if (level >= 40) return "text-orange-500";
    return "text-red-500";
  };

  const getMasteryBadge = (level: number) => {
    if (level >= 80) return <Badge className="bg-green-500">Mastered</Badge>;
    if (level >= 60) return <Badge className="bg-yellow-500">Proficient</Badge>;
    if (level >= 40) return <Badge className="bg-orange-500">Developing</Badge>;
    return <Badge className="bg-red-500">Needs Work</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" className="shrink-0" onClick={() => navigate(`/project/${courseId}`)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <GraduationCap className="h-7 w-7 sm:h-8 sm:w-8 text-primary shrink-0" />
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold truncate">Instructor Dashboard</h1>
            {dashboardData && (
              <p className="text-sm text-muted-foreground truncate">{dashboardData.course.title}</p>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {loading ? (
          <DashboardSkeleton />
        ) : dashboardData ? (
          <div className="space-y-8">
            {/* Summary Stats */}
            <section>
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                Course Overview
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                  icon={<Users className="h-5 w-5" />}
                  label="Enrolled Students"
                  value={dashboardData.summary.totalStudents}
                  color="text-blue-500"
                />
                <StatCard
                  icon={<TrendingUp className="h-5 w-5" />}
                  label="Avg Course Progress"
                  value={`${dashboardData.summary.averageProgress}%`}
                  color="text-green-500"
                />
                <StatCard
                  icon={<Target className="h-5 w-5" />}
                  label="Avg Objective Mastery"
                  value={`${dashboardData.summary.averageObjectiveMastery}%`}
                  color="text-purple-500"
                />
              </div>
            </section>
            {/* ── Analytics Charts ── */}
            {dashboardData.students.length > 1 && (
              <section>
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  Cohort Analytics
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                  {/* Score distribution histogram */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Score Distribution</CardTitle>
                      <CardDescription>How students are spread across mastery bands</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        const bands = [
                          { label: "0–20%", min: 0, max: 20, color: "#ef4444" },
                          { label: "21–40%", min: 21, max: 40, color: "#f97316" },
                          { label: "41–60%", min: 41, max: 60, color: "#f59e0b" },
                          { label: "61–80%", min: 61, max: 80, color: "#84cc16" },
                          { label: "81–100%", min: 81, max: 100, color: "#22c55e" },
                        ];
                        const data = bands.map(b => ({
                          label: b.label,
                          color: b.color,
                          count: dashboardData.students.filter(
                            s => s.averageObjectiveMastery >= b.min && s.averageObjectiveMastery <= b.max
                          ).length,
                        }));
                        return (
                          <ResponsiveContainer width="100%" height={180}>
                            <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} />
                              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                              <Tooltip formatter={(v: number) => [`${v} student${v !== 1 ? "s" : ""}`, "Count"]} />
                              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        );
                      })()}
                    </CardContent>
                  </Card>

                  {/* Student ranking chart */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Student Ranking</CardTitle>
                      <CardDescription>Objective mastery vs. class average</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        const avg = dashboardData.summary.averageObjectiveMastery;
                        const sorted = [...dashboardData.students]
                          .sort((a, b) => b.averageObjectiveMastery - a.averageObjectiveMastery)
                          .slice(0, 15)
                          .map(s => ({
                            name: s.email.split("@")[0].slice(0, 14),
                            score: s.averageObjectiveMastery,
                          }));
                        return (
                          <ResponsiveContainer width="100%" height={Math.max(160, sorted.length * 26)}>
                            <BarChart data={sorted} layout="vertical" margin={{ left: 0, right: 32, top: 4, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                              <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 10 }} />
                              <YAxis type="category" dataKey="name" width={88} tick={{ fontSize: 11 }} />
                              <Tooltip formatter={(v: number) => [`${v}%`, "Mastery"]} />
                              <ReferenceLine x={avg} stroke="#6366f1" strokeDasharray="4 2"
                                label={{ value: `avg ${avg}%`, position: "top", fontSize: 10, fill: "#6366f1" }} />
                              <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                                {sorted.map((entry, i) => (
                                  <Cell key={i} fill={
                                    entry.score >= 80 ? "#22c55e" :
                                    entry.score >= 60 ? "#84cc16" :
                                    entry.score >= 40 ? "#f59e0b" : "#ef4444"
                                  } />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </div>

                {/* Node difficulty chart */}
                {dashboardData.nodeDifficulty && dashboardData.nodeDifficulty.length > 0 && (
                  <Card className="mt-6">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Node Difficulty</CardTitle>
                      <CardDescription>
                        Pass rate per lesson across all students — sorted hardest first.
                        Nodes below 60% may need content or exam review.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        const data = dashboardData.nodeDifficulty!
                          .slice(0, 20)
                          .map(n => ({
                            name: n.title.length > 26 ? n.title.slice(0, 26) + "…" : n.title,
                            passRate: n.passRate,
                            avgScore: n.avgScore,
                            attempts: n.attempts,
                          }));
                        return (
                          <ResponsiveContainer width="100%" height={Math.max(200, data.length * 28)}>
                            <BarChart data={data} layout="vertical" margin={{ left: 0, right: 48, top: 4, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                              <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 10 }} />
                              <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                              <Tooltip
                                formatter={(_v: number, key: string, props: any) => {
                                  const d = props.payload;
                                  return key === "passRate"
                                    ? [`${d.passRate}% pass rate · avg score ${d.avgScore}% · ${d.attempts} attempt${d.attempts !== 1 ? "s" : ""}`, ""]
                                    : null;
                                }}
                              />
                              <ReferenceLine x={60} stroke="#f59e0b" strokeDasharray="4 2"
                                label={{ value: "60%", position: "top", fontSize: 10, fill: "#f59e0b" }} />
                              <Bar dataKey="passRate" radius={[0, 4, 4, 0]}>
                                {data.map((entry, i) => (
                                  <Cell key={i} fill={
                                    entry.passRate >= 80 ? "#22c55e" :
                                    entry.passRate >= 60 ? "#84cc16" :
                                    entry.passRate >= 40 ? "#f59e0b" : "#ef4444"
                                  } />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        );
                      })()}
                    </CardContent>
                  </Card>
                )}
              </section>
            )}

            {/* Learning Objectives Progress */}
            {dashboardData.objectives.length > 0 && (
              <section>
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Learning Objectives Performance
                </h2>
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {dashboardData.objectives.map((objective) => (
                        <div key={objective.objectiveId} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-medium">{objective.title}</p>
                              <p className="text-sm text-muted-foreground">
                                {objective.studentsAttempted} students attempted
                              </p>
                            </div>
                            <div className="text-right">
                              <p className={`text-lg font-bold ${getMasteryColor(objective.averageMastery)}`}>
                                {objective.averageMastery}%
                              </p>
                              <p className="text-xs text-muted-foreground">avg mastery</p>
                            </div>
                          </div>
                          <Progress value={objective.averageMastery} className="h-2" />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </section>
            )}

            {/* Add Students */}
            <section>
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-primary" />
                Add Students
              </h2>
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      type="email"
                      placeholder="student@example.com"
                      value={addEmail}
                      onChange={(e) => setAddEmail(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addStudents(addEmail)}
                      disabled={adding}
                      className="flex-1"
                    />
                    <Button
                      onClick={() => addStudents(addEmail)}
                      disabled={adding || !addEmail.trim()}
                    >
                      {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
                    </Button>
                  </div>
                  <div>
                    <Textarea
                      placeholder="Or paste multiple emails (one per line, or comma/semicolon separated)"
                      value={batchEmails}
                      onChange={(e) => setBatchEmails(e.target.value)}
                      disabled={adding}
                      className="min-h-[80px] text-sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => addStudents(batchEmails)}
                      disabled={adding || !batchEmails.trim()}
                    >
                      {adding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Add all
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Students must have an account. Add them by email; they will be enrolled and can access the course.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Student Table */}
            <section>
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Student Progress
              </h2>
              {dashboardData.students.length === 0 && (!dashboardData.invited || dashboardData.invited.length === 0) ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No students enrolled yet. Add learners by email; they can sign up when they receive the invite link.</p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table className="min-w-[600px]">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Student</TableHead>
                          <TableHead>Course Progress</TableHead>
                          <TableHead>Objective Mastery</TableHead>
                          <TableHead>Exams</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="w-[80px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dashboardData.students.map((student) => (
                          <TableRow
                            key={student.userId}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => navigate(`/instructor/${courseId}/students/${student.userId}`)}
                          >
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div>
                                  <p className="font-medium">{student.email}</p>
                                  <p className="text-xs text-muted-foreground">
                                    Enrolled {new Date(student.enrolledAt).toLocaleDateString()}
                                  </p>
                                </div>
                                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <Progress value={student.progress.progressPercentage} className="h-2 w-20" />
                                  <span className="text-sm">{student.progress.progressPercentage}%</span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {student.progress.completedNodes}/{student.progress.totalNodes} skills
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${getMasteryColor(student.averageObjectiveMastery)}`}>
                                  {student.averageObjectiveMastery}%
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div className="flex items-center gap-1">
                                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                                  {student.examStats.passedExams} passed
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  Avg score: {student.examStats.averageScore}%
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              {getMasteryBadge(student.averageObjectiveMastery)}
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    disabled={removing === student.userId}
                                  >
                                    {removing === student.userId ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4" />
                                    )}
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Remove student</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Remove {student.email} from this course? They will lose access immediately.
                                      This cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      onClick={() => removeStudent(student)}
                                    >
                                      Remove
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Invited (pending sign-up) */}
              {dashboardData.invited && dashboardData.invited.length > 0 && (
                <Card className="mt-4">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <UserPlus className="h-4 w-4 text-amber-500" />
                      Invited — pending sign-up
                    </CardTitle>
                    <CardDescription>
                      These people have been sent a sign-up link. They will appear as students once they create an account.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {dashboardData.invited.map((inv) => (
                        <li
                          key={inv.email}
                          className="flex items-center justify-between gap-2 py-2 border-b border-muted/50 last:border-0"
                        >
                          <div>
                            <p className="font-medium">{inv.email}</p>
                            <p className="text-xs text-muted-foreground">
                              Invited {new Date(inv.invited_at).toLocaleDateString()}
                            </p>
                          </div>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                disabled={removingInvite === inv.email}
                              >
                                {removingInvite === inv.email ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove invitation</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Remove the invitation for {inv.email}? They will no longer be able to sign up to join this course via the invite link.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  onClick={() => removeInvitation(inv.email)}
                                >
                                  Remove
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </section>

            {/* Student Stats Sheet - opens when clicking a student row */}
            <Sheet open={!!selectedStudent} onOpenChange={(open) => !open && setSelectedStudent(null)}>
              <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
                {selectedStudent && (
                  <>
                    <SheetHeader>
                      <SheetTitle>{selectedStudent.email}</SheetTitle>
                      <SheetDescription>
                        Enrolled {new Date(selectedStudent.enrolledAt).toLocaleDateString()} • {getMasteryBadge(selectedStudent.averageObjectiveMastery)}
                      </SheetDescription>
                    </SheetHeader>
                    <div className="mt-6 space-y-6">
                      <div className="grid grid-cols-2 gap-4">
                        <Card>
                          <CardContent className="pt-4">
                            <p className="text-2xl font-bold">{selectedStudent.progress.progressPercentage}%</p>
                            <p className="text-sm text-muted-foreground">Course Progress</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {selectedStudent.progress.completedNodes}/{selectedStudent.progress.totalNodes} skills completed
                            </p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="pt-4">
                            <p className={`text-2xl font-bold ${getMasteryColor(selectedStudent.averageObjectiveMastery)}`}>
                              {selectedStudent.averageObjectiveMastery}%
                            </p>
                            <p className="text-sm text-muted-foreground">Objective Mastery</p>
                          </CardContent>
                        </Card>
                      </div>
                      <div>
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          Exam Results
                        </h4>
                        <Card>
                          <CardContent className="pt-4">
                            <p className="text-sm">
                              <span className="font-medium">{selectedStudent.examStats.passedExams}</span> passed
                              {selectedStudent.examStats.totalExamAttempts > 0 && (
                                <> • Avg score: <span className="font-medium">{selectedStudent.examStats.averageScore}%</span></>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {selectedStudent.examStats.totalExamAttempts} total exam attempt(s)
                            </p>
                          </CardContent>
                        </Card>
                      </div>
                      {selectedStudent.objectiveMasteries.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2 flex items-center gap-2">
                            <Target className="h-4 w-4 text-primary" />
                            Objective Mastery by Topic
                          </h4>
                          <div className="space-y-2">
                            {selectedStudent.objectiveMasteries.map((obj) => (
                              <div
                                key={obj.objectiveId}
                                className="p-3 rounded-lg bg-muted/50 space-y-1"
                              >
                                <p className="text-sm font-medium truncate" title={obj.objectiveTitle}>
                                  {obj.objectiveTitle}
                                </p>
                                <div className="flex items-center justify-between gap-2">
                                  <Progress value={obj.masteryLevel} className="h-2 flex-1" />
                                  <span className={`text-sm font-bold shrink-0 ${getMasteryColor(obj.masteryLevel)}`}>
                                    {obj.masteryLevel}%
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {obj.correctCount}/{obj.attemptsCount} correct
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="pt-4 border-t">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="destructive"
                              size="sm"
                              className="w-full"
                              disabled={removing === selectedStudent.userId}
                            >
                              {removing === selectedStudent.userId ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              ) : (
                                <Trash2 className="h-4 w-4 mr-2" />
                              )}
                              Remove from course
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove student</AlertDialogTitle>
                              <AlertDialogDescription>
                                Remove {selectedStudent.email} from this course? They will lose access immediately.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() => removeStudent(selectedStudent)}
                              >
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </>
                )}
              </SheetContent>
            </Sheet>

            {/* Objective × Student Heat Map */}
            {dashboardData.students.length > 0 && dashboardData.objectives.length > 0 && (
              <section>
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <Award className="h-5 w-5 text-primary" />
                  Objective Mastery Heat Map
                </h2>
                <Card>
                  <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b bg-muted/40">
                          <th className="text-left px-4 py-3 font-medium sticky left-0 bg-muted/40 z-10 min-w-[160px]">
                            Student
                          </th>
                          <th className="text-center px-3 py-3 font-medium min-w-[72px] whitespace-nowrap">
                            Overall
                          </th>
                          {dashboardData.objectives.map(obj => (
                            <th key={obj.objectiveId}
                              className="text-center px-2 py-3 font-medium min-w-[90px] max-w-[120px]"
                              title={obj.title}>
                              <div className="truncate text-xs leading-tight max-w-[110px]">
                                {obj.title.length > 18 ? obj.title.slice(0, 18) + "…" : obj.title}
                              </div>
                              <div className="text-[10px] text-muted-foreground font-normal mt-0.5">
                                avg {obj.averageMastery}%
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[...dashboardData.students]
                          .sort((a, b) => b.averageObjectiveMastery - a.averageObjectiveMastery)
                          .map(student => (
                          <tr key={student.userId}
                            className="border-b last:border-0 hover:bg-muted/20 cursor-pointer"
                            onClick={() => navigate(`/instructor/${courseId}/students/${student.userId}`)}>
                            <td className="px-4 py-2 sticky left-0 bg-background z-10">
                              <div className="flex items-center gap-1.5">
                                <span className="truncate max-w-[140px] text-sm" title={student.email}>
                                  {student.email}
                                </span>
                                <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                              </div>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <HeatCell value={student.averageObjectiveMastery} bold />
                            </td>
                            {dashboardData.objectives.map(obj => {
                              const m = student.objectiveMasteries.find(o => o.objectiveId === obj.objectiveId);
                              return (
                                <td key={obj.objectiveId} className="px-2 py-2 text-center">
                                  <HeatCell value={m?.masteryLevel ?? 0} attempted={(m?.attemptsCount ?? 0) > 0} />
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                        {/* Class average row */}
                        <tr className="border-t-2 bg-muted/30">
                          <td className="px-4 py-2 sticky left-0 bg-muted/30 z-10">
                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                              Class avg
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <HeatCell value={dashboardData.summary.averageObjectiveMastery} bold />
                          </td>
                          {dashboardData.objectives.map(obj => (
                            <td key={obj.objectiveId} className="px-2 py-2 text-center">
                              <HeatCell value={obj.averageMastery} />
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-4 flex-wrap">
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-[#dcfce7]"/>80–100% Mastered</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-[#d9f99d]"/>60–79% Proficient</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-[#fef08a]"/>40–59% Developing</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-[#fed7aa]"/>1–39% Struggling</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-muted border"/>0% No data</span>
                </p>
              </section>
            )}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Failed to load dashboard data.</p>
              <Button onClick={fetchDashboardData} className="mt-4">
                Retry
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

function HeatCell({ value, bold, attempted }: { value: number; bold?: boolean; attempted?: boolean }) {
  const bg = value >= 80 ? "bg-[#dcfce7] text-green-800"
    : value >= 60 ? "bg-[#d9f99d] text-lime-800"
    : value >= 40 ? "bg-[#fef08a] text-yellow-800"
    : value > 0 ? "bg-[#fed7aa] text-orange-800"
    : attempted ? "bg-[#fee2e2] text-red-400"
    : "bg-muted text-muted-foreground";
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs ${bold ? "font-bold" : "font-medium"} ${bg}`}>
      {value > 0 || attempted ? `${value}%` : "—"}
    </span>
  );
}

function StatCard({
  icon, 
  label, 
  value, 
  color 
}: { 
  icon: React.ReactNode; 
  label: string; 
  value: string | number; 
  color: string;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-full bg-muted ${color}`}>
            {icon}
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-sm text-muted-foreground">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-8">
      <section>
        <Skeleton className="h-8 w-48 mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div>
                    <Skeleton className="h-6 w-16 mb-1" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
      <section>
        <Skeleton className="h-8 w-64 mb-4" />
        <Card>
          <CardContent className="pt-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="mb-4">
                <Skeleton className="h-4 w-48 mb-2" />
                <Skeleton className="h-2 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
